import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useState, useRef } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from '../../../components/Toolbar/Toolbar.module.css';
import classNames from 'classnames';
import { isResizableElement } from '../../../types/guards';
import useFocusElementToolbarInput from '../../../components/ElementToolbar/useFocusElementToolbarInput';

const EditSizeModal = ({ editor, element, toolbarHideModal }: ToolArgs) => {
  const defaultWidth =
    element && isResizableElement(element) ? element.resizer_width : undefined;
  const defaultHeight =
    element && isResizableElement(element) ? element.resizer_height : undefined;

  const [originalWidth] = useState(defaultWidth);
  const [width, setWidth] = useState(defaultWidth);
  const [originalHeight] = useState(defaultHeight);
  const [height, setHeight] = useState(defaultHeight);
  const inputRef = useRef<HTMLInputElement>(null);

  useFocusElementToolbarInput(inputRef);

  if (!element) return <></>;

  const path = ReactEditor.findPath(editor, element);

  const updateElementSize = (width, height) => {
    Transforms.setNodes(
      editor,
      {
        resizer_width: parseInt(width) || originalWidth,
        resizer_height: parseInt(height) || originalHeight,
        resizer_originalWidth: parseInt(width) || originalWidth,
        resizer_originalHeight: parseInt(height) || originalHeight,
      },
      {
        at: path,
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        toolbarHideModal?.();
        event.preventDefault();
        updateElementSize(width, height);
        break;

      case 'Tab':
        updateElementSize(width, height);
        break;
      case 'Escape':
        toolbarHideModal?.();
        event.preventDefault();
        updateElementSize(originalWidth, originalHeight);
        break;
    }
  };

  const handleBlur = () => {
    updateElementSize(width, height);
  };

  return (
    <div>
      <span>Width: </span>
      <input
        type="integer"
        ref={inputRef}
        className={classNames('unstyled-input', styles.toolbarInput)}
        style={{
          width: 40,
          border: '1px solid var(--theme-secondary-border)',
        }}
        value={width}
        onChange={(event) => setWidth(Number(event.target.value))}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
      <span style={{ marginLeft: 10 }}>Height: </span>
      <input
        type="integer"
        className={classNames('unstyled-input', styles.toolbarInput)}
        style={{
          width: 40,
          border: '1px solid var(--theme-secondary-border)',
        }}
        value={height}
        onChange={(event) => setHeight(Number(event.target.value))}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

// Edit the width and height of an embed.
class ToolEditSize extends Tool {
  static toolName = 'Adjust dimensions';
  static iconClass = 'fa-regular fa-ruler-horizontal';
  static withoutRefocus = true;

  // Show the modal view to editor the URL.
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolEditSize);
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return EditSizeModal(toolArgs);
  }
}

export default ToolEditSize;
